import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, X } from 'lucide-react'
import { PageHeader } from '@/components/layout/PageHeader'
import { Breadcrumb } from '@/components/layout/Breadcrumb'
import { KpiCard } from '@/components/layout/KpiCard'
import { PageToolbar } from '@/components/layout/PageToolbar'
import { Button } from '@/components/ui/Button'
import { StatusBadge } from '@/components/ui/StatusBadge'
import { PageError } from '@/components/ui/PageAsyncState'
import { ProtoExcelTable, type ProtoColumn } from '@/components/table/ProtoExcelTable'
import { formatDate, formatDateFull } from '@/lib/dateFormat'
import {
  packagingBomService,
  type PackagingBom,
  type PackagingBomStatus,
} from '@/services/packagingBoms'

const fieldClass =
  'h-8 rounded-[var(--radius-sm)] border border-[var(--color-border)] bg-white px-2 text-sm outline-none focus:border-[var(--color-accent)]'

export function PackagingBomListPage() {
  const navigate = useNavigate()
  const [rows, setRows] = useState<PackagingBom[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<'' | PackagingBomStatus>('')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [createName, setCreateName] = useState('')
  const [createCodeHint, setCreateCodeHint] = useState('')
  const [createNotes, setCreateNotes] = useState('')
  const [creating, setCreating] = useState(false)

  async function reload() {
    setLoading(true)
    setError(null)
    try {
      setRows(await packagingBomService.list())
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Không tải được danh sách Bộ đóng gói',
      )
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void reload()
  }, [])

  const kpis = useMemo(() => {
    let active = 0
    let inactive = 0
    for (const r of rows) {
      if (r.status === 'active') active += 1
      else if (r.status === 'inactive') inactive += 1
    }
    return { total: rows.length, active, inactive }
  }, [rows])

  const filtered = useMemo(() => {
    if (!statusFilter) return rows
    return rows.filter((r) => r.status === statusFilter)
  }, [rows, statusFilter])

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!createName.trim()) return
    setCreating(true)
    try {
      const created = await packagingBomService.create({
        name: createName.trim(),
        code_hint: createCodeHint.trim() || null,
        notes: createNotes.trim() || null,
      })
      setShowCreateModal(false)
      setCreateName('')
      setCreateCodeHint('')
      setCreateNotes('')
      navigate(`/purchasing/packaging-boms/${created.id}`)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Tạo Bộ đóng gói thất bại',
      )
    } finally {
      setCreating(false)
    }
  }

  const columns: ProtoColumn<PackagingBom>[] = useMemo(
    () => [
      {
        id: 'code_hint',
        header: 'Mã gợi ý',
        sortable: true,
        cell: (r) => (
          <span className="font-mono text-xs font-semibold text-slate-800">
            {r.code_hint || '—'}
          </span>
        ),
        sortValue: (r) => r.code_hint ?? '',
      },
      {
        id: 'name',
        header: 'Tên bộ đóng gói',
        sortable: true,
        hideable: false,
        width: 260,
        cell: (r) => <span className="font-medium text-slate-900">{r.name}</span>,
        sortValue: (r) => r.name,
      },
      {
        id: 'version',
        header: 'Phiên bản',
        sortable: true,
        cell: (r) => (
          <span>
            {r.version_label}{' '}
            <span className="text-slate-400">
              (v{r.version_seq}
              {r.edit_count ? ` · sửa ${r.edit_count}` : ''})
            </span>
          </span>
        ),
        sortValue: (r) => r.version_seq,
      },
      {
        id: 'status',
        header: 'Trạng thái',
        sortable: true,
        cell: (r) => (
          <StatusBadge
            tone={r.status === 'active' ? 'success' : 'neutral'}
            label={r.status.toUpperCase()}
            showDot={false}
          />
        ),
        sortValue: (r) => r.status,
      },
      {
        id: 'updated_at',
        header: 'Cập nhật',
        sortable: true,
        cell: (r) => (
          <span
            className="text-slate-500"
            title={r.updated_at ? formatDateFull(r.updated_at) : undefined}
          >
            {r.updated_at ? formatDate(r.updated_at) : '—'}
          </span>
        ),
        sortValue: (r) => r.updated_at ?? '',
      },
    ],
    [],
  )

  return (
    <div className="space-y-4">
      <Breadcrumb
        items={[
          { label: 'Trang chủ', to: '/' },
          { label: 'Mua hàng', to: '/purchasing' },
          { label: 'Bộ đóng gói (Packaging BOM)' },
        ]}
      />
      <PageHeader
        title="Bộ đóng gói (Packaging BOM)"
        subtitle="Quản lý định mức đóng gói độc lập theo sản phẩm, mode và tiêu chuẩn ISTA"
        actions={
          <Button onClick={() => setShowCreateModal(true)}>
            <Plus className="h-4 w-4" />
            Tạo mới Bộ đóng gói
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-2 xl:grid-cols-3">
        <KpiCard label="Tổng bộ đóng gói" value={kpis.total} />
        <KpiCard label="Active" value={kpis.active} tone="success" />
        <KpiCard
          label="Inactive"
          value={kpis.inactive}
          tone={kpis.inactive ? 'warning' : 'default'}
        />
      </div>

      {error ? (
        <PageError message={error} onRetry={() => void reload()} />
      ) : null}

      <PageToolbar
        left={
          <select
            className={fieldClass}
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as '' | PackagingBomStatus)
            }
            aria-label="Lọc trạng thái"
          >
            <option value="">Tất cả trạng thái</option>
            <option value="active">ACTIVE</option>
            <option value="inactive">INACTIVE</option>
            <option value="archived">ARCHIVED</option>
          </select>
        }
      />

      <ProtoExcelTable
        showRowNumbers
        columns={columns}
        rows={filtered}
        rowKey={(r) => r.id}
        searchPlaceholder="Tìm tên, mã gợi ý đóng gói…"
        exportName="bo-dong-goi-packaging-bom"
        pageSize={50}
        loading={loading}
        emptyMessage="Chưa có Bộ đóng gói nào."
        onRowClick={(r) => navigate(`/purchasing/packaging-boms/${r.id}`)}
        stickyLeftColumnIds={['name']}
      />

      {/* Modal Tạo mới Bộ đóng gói */}
      {showCreateModal ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl space-y-4">
            <div className="flex items-center justify-between border-b border-slate-100 pb-3">
              <h3 className="text-base font-bold text-slate-900">
                Tạo mới Bộ đóng gói (Packaging BOM)
              </h3>
              <button
                type="button"
                onClick={() => setShowCreateModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={(e) => void handleCreate(e)} className="space-y-3 text-xs">
              <label className="block">
                <span className="font-semibold text-slate-700">Tên bộ đóng gói *</span>
                <input
                  required
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                  value={createName}
                  onChange={(e) => setCreateName(e.target.value)}
                  placeholder="VD: Gói thùng carton MP154..."
                />
              </label>

              <label className="block">
                <span className="font-semibold text-slate-700">Mã gợi ý (Code hint)</span>
                <input
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-indigo-500 font-mono"
                  value={createCodeHint}
                  onChange={(e) => setCreateCodeHint(e.target.value)}
                  placeholder="VD: MP154-PACK"
                />
              </label>

              <label className="block">
                <span className="font-semibold text-slate-700">Ghi chú</span>
                <textarea
                  rows={2}
                  className="mt-1 w-full rounded border border-slate-300 px-3 py-1.5 text-xs outline-none focus:border-indigo-500"
                  value={createNotes}
                  onChange={(e) => setCreateNotes(e.target.value)}
                  placeholder="Ghi chú kỹ thuật đóng gói..."
                />
              </label>

              <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => setShowCreateModal(false)}
                  disabled={creating}
                >
                  Hủy
                </Button>
                <Button type="submit" disabled={creating || !createName.trim()}>
                  {creating ? 'Đang tạo…' : 'Tạo mới'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  )
}
